import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { useDesignStore, type DesignSide } from '../../store/designStore';
import type { FabricCanvas } from '../../types/fabric';
import { buildTemplateThumbnailDataUrl } from '../../lib/designDownload';
import { isPrintableSurfaceMeshName, type GlbDesignPlacement, type ProductVariantDTO } from '@customedge/shared';

// Shared by the full 3D Preview tab (Model3DPreview.tsx) and the floating mini
// preview widget (Model3DPreviewMini.tsx) so both render an identical baked
// composite from one piece of code, rather than two textures that can drift
// out of sync with each other.

// Catches GLTFLoader failures (bad/corrupt .glb, 404) — without this, drei's
// useGLTF throws inside the R3F render tree and takes down the whole tab
// instead of just this preview panel.
export class ModelErrorBoundary extends React.Component<{ children: React.ReactNode; onError?: () => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onError?.(); }
  render() { return this.state.failed ? null : this.props.children; }
}

function loadImageEl(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load design texture image'));
    img.src = src;
  });
}

export const UV_CANVAS_SIZE = 1024;

// The 3D wrap must show only what the customer actually customized — their
// uploaded image, clipart, text, and any background they picked via the
// Background tool — never the product's own reference/mockup photo (the
// "slide image" used for the 2D preview tab). `buildTemplateThumbnailDataUrl`
// already renders just the Fabric canvas objects with a transparent backdrop.
//
// Each placeholder with a `glbBox` (admin-calibrated, see Model3DUVEditor in the
// admin app) gets baked into its own spot on ONE shared UV-space canvas — a
// product can have several placeholders (e.g. "Main" + "Cap") that each need to
// land in a different spot on the same model, not all in the same place, so this
// composites every placed one together rather than texturing only whichever 2D
// tab happens to be active. Expressing each box this way (baked pixels) rather
// than three.js's raw per-mesh texture offset/repeat/rotation is far simpler to
// reason about than the underlying UV-transform math, and is the only way to put
// more than one design on the same mesh's single texture map.
// `meshName` filters which placed sides actually get baked in: a side whose
// placement has no `targetMesh` set applies to every mesh (the original,
// still-correct behavior for a single-mesh model with several painted UV
// islands on one shared texture, e.g. a bottle's cap region vs. body region);
// a side with `targetMesh` set only bakes for the mesh of that exact name.
// Without this filter, a model with more than one printable mesh that happens
// to reuse the same UV space — e.g. a flag's separate front/back faces — gets
// every placed side's design pasted onto every one of those meshes, not just
// its own (the bug this filter exists to fix).
export async function buildCompositeDesignTexture(
  placedSides: DesignSide[],
  canvasInstances: Map<string, FabricCanvas>,
  sideColorCountJsonCache: Record<string, Record<string, any>>,
  resolveActiveColorCountKey: (side: DesignSide) => string,
  meshName: string,
): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width  = UV_CANVAS_SIZE;
  canvas.height = UV_CANVAS_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');
  // Deliberately left transparent (no background fill) — this gets layered
  // on top of each target mesh's own base-texture + Object-Variant composite
  // in `buildMeshCompositeTexture` below, and must only ever cover the
  // customer's actual placed design content, never blank out what's
  // underneath wherever nothing's been placed.

  for (const side of placedSides) {
    const placement = side.glbBox as GlbDesignPlacement;
    if (placement.targetMesh && placement.targetMesh !== meshName) continue;
    const transparentPng = await buildTemplateThumbnailDataUrl(side, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey);
    const img = await loadImageEl(transparentPng);

    const boxWidth  = placement.width  * UV_CANVAS_SIZE;
    const boxHeight = placement.height * UV_CANVAS_SIZE;
    const centerX   = placement.left * UV_CANVAS_SIZE + boxWidth / 2;
    const centerY   = placement.top  * UV_CANVAS_SIZE + boxHeight / 2;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((placement.rotationDeg * Math.PI) / 180);
    ctx.drawImage(img, -boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight);
    ctx.restore();
  }

  return canvas.toDataURL('image/png');
}

function loadImageElCached(url: string, cache: Map<string, HTMLImageElement>): Promise<HTMLImageElement> {
  const existing = cache.get(url);
  if (existing) return Promise.resolve(existing);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { cache.set(url, img); resolve(img); };
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

// Clips to a Variant's UV-space box (rotated about its own center, same
// convention `buildCompositeDesignTexture` already uses for design
// placeholders) and runs `draw` in a coordinate frame centered on the box
// with `halfW`/`halfH` extents — so callers don't have to redo the
// translate/rotate math themselves.
function paintBox(
  ctx: CanvasRenderingContext2D,
  box: GlbDesignPlacement,
  draw: (halfW: number, halfH: number) => void,
) {
  const w = box.width * UV_CANVAS_SIZE;
  const h = box.height * UV_CANVAS_SIZE;
  const cx = box.left * UV_CANVAS_SIZE + w / 2;
  const cy = box.top * UV_CANVAS_SIZE + h / 2;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((box.rotationDeg * Math.PI) / 180);
  ctx.beginPath();
  ctx.rect(-w / 2, -h / 2, w, h);
  ctx.clip();
  draw(w / 2, h / 2);
  ctx.restore();
}

// Bakes ONE composite color texture (plus, only if needed, one metalness/
// roughness map) per target mesh: the mesh's own original appearance as the
// base layer — preserved everywhere by default, e.g. the bottle stays its
// real photographed brown — then each Object Variant with a selection paints
// flatly into its own boxed UV region (a flat color, a texture image, or a
// customer-picked free color), and finally the customer's actual placed
// design content (already composited transparent-except-where-placed by
// `buildCompositeDesignTexture`) goes on top, last, so it's never obscured.
//
// A mesh with no base texture of its own, no Variants, and no placed design
// at all is left with a plain neutral material — there's nothing to bake.
export async function buildMeshCompositeTexture(
  baseImage: HTMLImageElement | ImageBitmap | undefined,
  baseColor: THREE.Color | undefined,
  variants: ProductVariantDTO[],
  selectedVariantOptions: Record<string, string>,
  variantImageCache: Map<string, HTMLImageElement>,
  designOverlayImg: HTMLImageElement | null,
): Promise<{ colorMapUrl: string; ormMapUrl: string | null }> {
  const colorCanvas = document.createElement('canvas');
  colorCanvas.width = UV_CANVAS_SIZE;
  colorCanvas.height = UV_CANVAS_SIZE;
  const ctx = colorCanvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');

  if (baseImage && (baseImage as any).width) {
    // Left fully transparent (no pre-fill) before drawing -- some real vendor
    // GLBs (e.g. PFConcept's "imprint" placeholder meshes, a genuinely
    // see-through print window -- see history below) ship a base texture
    // that's mostly/fully alpha=0 ON PURPOSE, meant to show through to
    // whatever's behind the mesh until a design is placed on top. A white
    // (or any opaque) pre-fill here would defeat that -- `drawImage`'s
    // default "source-over" compositing keeps the destination's opaque alpha
    // wherever the source is transparent, making the whole mesh solid
    // instead of see-through. The resulting PNG's alpha channel is honored
    // by the GPU only if the material below sets `transparent: true` (from
    // the ORIGINAL mesh material's own alphaMode, captured as
    // `__origTransparent` in TexturedModel) -- without that flag, WebGL
    // ignores alpha and renders the (transparent-canvas-default) RGB of
    // (0,0,0) as solid BLACK regardless of what's drawn here.
    ctx.drawImage(baseImage as any, 0, 0, UV_CANVAS_SIZE, UV_CANVAS_SIZE);
  } else {
    // Many real product GLBs (e.g. a pen with separate flat-colored parts)
    // have NO diffuse texture at all — the mesh's true color lives only in
    // its material's flat `baseColorFactor` (glTF) / `.color` (three.js),
    // never in `.map`. Falling back to a hardcoded gray here would discard
    // that real color and show every such mesh as flat gray, even though
    // the admin's untouched-material viewer renders it correctly.
    ctx.fillStyle = baseColor ? `#${baseColor.getHexString()}` : '#d4d4d8';
    ctx.fillRect(0, 0, UV_CANVAS_SIZE, UV_CANVAS_SIZE);
  }

  let hasMetallic = false;
  let ormCtx: CanvasRenderingContext2D | null = null;

  for (const variant of variants) {
    if (!variant.regions.length) continue;
    const selection = selectedVariantOptions[variant.id];
    if (!selection) continue; // nothing picked yet — let the base layer show through

    // Every region this Variant owns (e.g. a cap's "Top" disc AND its "Side"
    // cylinder) gets painted identically — one customer-facing choice, applied
    // everywhere it's relevant, not just the first box.
    if (selection.startsWith('custom:')) {
      for (const region of variant.regions) {
        paintBox(ctx, region, (hw, hh) => {
          ctx.fillStyle = selection.slice(7);
          ctx.fillRect(-hw, -hh, hw * 2, hh * 2);
        });
      }
      continue;
    }

    const option = variant.options.find(o => o.id === selection);
    if (!option) continue;

    if (option.type === 'texture' && option.textureUrl) {
      const img = await loadImageElCached(option.textureUrl, variantImageCache).catch(() => null);
      if (img) for (const region of variant.regions) {
        paintBox(ctx, region, (hw, hh) => ctx.drawImage(img, -hw, -hh, hw * 2, hh * 2));
      }
    } else if (option.colorHex) {
      for (const region of variant.regions) {
        paintBox(ctx, region, (hw, hh) => {
          ctx.fillStyle = option.colorHex!;
          ctx.fillRect(-hw, -hh, hw * 2, hh * 2);
        });
      }
    }

    if (option.type === 'metallic') {
      hasMetallic = true;
      if (!ormCtx) {
        const ormCanvas = document.createElement('canvas');
        ormCanvas.width = UV_CANVAS_SIZE;
        ormCanvas.height = UV_CANVAS_SIZE;
        ormCtx = ormCanvas.getContext('2d');
        if (ormCtx) {
          // glTF ORM convention: R=occlusion (unused, full white), G=roughness, B=metalness.
          // Default everywhere: roughness 1, metalness 0 — i.e. not metallic.
          ormCtx.fillStyle = 'rgb(255,255,0)';
          ormCtx.fillRect(0, 0, UV_CANVAS_SIZE, UV_CANVAS_SIZE);
        }
      }
      if (ormCtx) {
        const ctx2 = ormCtx;
        for (const region of variant.regions) {
          paintBox(ctx2, region, (hw, hh) => {
            ctx2.fillStyle = 'rgb(255,51,230)'; // roughness ~0.2, metalness ~0.9
            ctx2.fillRect(-hw, -hh, hw * 2, hh * 2);
          });
        }
      }
    }
  }

  if (designOverlayImg) ctx.drawImage(designOverlayImg, 0, 0, UV_CANVAS_SIZE, UV_CANVAS_SIZE);

  return {
    colorMapUrl: colorCanvas.toDataURL('image/png'),
    ormMapUrl: hasMetallic && ormCtx ? (ormCtx.canvas as HTMLCanvasElement).toDataURL('image/png') : null,
  };
}

// glTF's UV convention has V increasing downward (origin top-left) — a manually
// constructed THREE.Texture defaults `flipY: true` (the convention for non-glTF
// assets), which mirrors the image vertically. GLTFLoader's own textures already
// set this to false for the same reason; anything loaded by hand (a baked
// composite, a Variant's texture-type option image) needs it set explicitly.
function loadFlatTexture(url: string): THREE.Texture {
  const tex = new THREE.TextureLoader().load(url);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.flipY = false;
  return tex;
}

export function TexturedModel({ url, designOverlayUrl, variants, selectedVariantOptions }: {
  url: string;
  // Built fresh per target mesh, not shared globally — see the `meshName`
  // filter in `buildCompositeDesignTexture` for why one shared overlay can't
  // be reused across every mesh once a model has more than one printable one.
  designOverlayUrl: (meshName: string) => Promise<string | null>;
  variants: ProductVariantDTO[];
  selectedVariantOptions: Record<string, string>;
}) {
  const { scene } = useGLTF(url);
  // Each target mesh's ORIGINAL base image, captured once when the scene
  // first loads — `mesh.material` gets overwritten with our own baked
  // composite below, so without this snapshot a second bake (any selection
  // change) would read back our OWN previous composite as if it were the
  // model's real shipped appearance, compounding bakes on top of each other
  // instead of always starting fresh from the truth.
  //
  // IMPORTANT: this snapshot must live on the mesh's own `userData`, not in a
  // component-local `useRef` — drei's `useGLTF` caches the parsed scene
  // globally by URL, so the SAME `THREE.Mesh` instances are reused every time
  // this component mounts (e.g. switching the Design tab's floating mini
  // widget open/closed, or navigating to the full "3D Preview" tab and back —
  // both unmount/remount this component, but get the identical cached mesh
  // back). A `useRef` resets on every remount and would re-snapshot whatever
  // `mesh.material.map` happens to be at that moment — which, after the first
  // bake ever ran, is already OUR composite, not the model's real shipped
  // texture — so each remount would stack one more copy of the customer's
  // design on top of the last. Tagging the mesh itself makes the snapshot
  // genuinely "once per mesh for the page's lifetime," matching the comment's
  // original intent.
  const variantImageCache = useRef(new Map<string, HTMLImageElement>());
  const [, setBakeTick] = useState(0);

  const targetMeshes = useMemo(() => {
    const all: THREE.Mesh[] = [];
    scene.traverse(obj => { if ((obj as any).isMesh) all.push(obj as THREE.Mesh); });
    const named = all.filter(m => isPrintableSurfaceMeshName(m.name));
    return named.length ? named : all;
  }, [scene]);

  useEffect(() => {
    for (const mesh of targetMeshes) {
      if (mesh.userData.__origBaseCaptured) continue;
      const mat = mesh.material as THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[];
      const single = Array.isArray(mat) ? mat[0] : mat;
      mesh.userData.__origBaseImage = single?.map?.image as (HTMLImageElement | ImageBitmap | undefined);
      mesh.userData.__origBaseColor = single?.color ? single.color.clone() : undefined;
      // GLTFLoader sets `.transparent` (from the glTF material's alphaMode
      // BLEND/MASK) on the mesh's ORIGINAL material -- capture it so the
      // rebaked material below can preserve it. Some real vendor GLBs (e.g.
      // PFConcept's "imprint" placeholder meshes -- see history below) ship a
      // genuinely see-through mesh: a fully-transparent (alpha=0) base
      // texture meant to show whatever design gets placed on top, or nothing
      // at all if no design is placed yet.
      mesh.userData.__origTransparent = single?.transparent ?? false;
      mesh.userData.__origBaseCaptured = true;
    }
  }, [targetMeshes]);

  useEffect(() => {
    let cancelled = false;

    async function bake() {
      for (const mesh of targetMeshes) {
        // Each mesh gets its OWN overlay built fresh for it — not one shared
        // overlay reused across every mesh — so a placement aimed at one
        // specific mesh (e.g. a flag's back face) never bleeds onto another
        // mesh that happens to share the same UV space (its front face).
        const meshOverlayUrl = await designOverlayUrl(mesh.name);
        const designOverlayImg = meshOverlayUrl
          ? await loadImageElCached(meshOverlayUrl, variantImageCache.current).catch(() => null)
          : null;
        if (cancelled) return;

        const baseImage = mesh.userData.__origBaseImage as (HTMLImageElement | ImageBitmap | undefined);
        const baseColor = mesh.userData.__origBaseColor as (THREE.Color | undefined);
        const { colorMapUrl, ormMapUrl } = await buildMeshCompositeTexture(
          baseImage, baseColor, variants, selectedVariantOptions, variantImageCache.current, designOverlayImg,
        );
        if (cancelled) return;

        const colorTex = loadFlatTexture(colorMapUrl);
        const ormTex = ormMapUrl ? loadFlatTexture(ormMapUrl) : null;
        // Preserve the ORIGINAL mesh's transparency intent (see
        // __origTransparent above) -- MeshStandardMaterial defaults to
        // `transparent: false`, which makes WebGL ignore the composited
        // texture's alpha channel entirely and render alpha=0 pixels as
        // solid black instead of see-through.
        const origTransparent = mesh.userData.__origTransparent as boolean;
        mesh.material = new THREE.MeshStandardMaterial({
          map: colorTex,
          color: 0xffffff,
          transparent: origTransparent,
          ...(ormTex ? { metalnessMap: ormTex, roughnessMap: ormTex, metalness: 1, roughness: 1 } : { metalness: 0, roughness: 1 }),
        });
      }
      if (!cancelled) setBakeTick(n => n + 1);
    }

    bake();
    return () => { cancelled = true; };
  }, [targetMeshes, designOverlayUrl, variants, selectedVariantOptions]);

  return <primitive object={scene} />;
}

// Every placeholder the admin has positioned on the 3D model — not just
// whichever 2D tab happens to be active — composited into one live-updating
// overlay texture, built separately per target mesh (see the `targetMesh`
// filter in `buildCompositeDesignTexture`). Shared by the full 3D Preview tab
// and the mini widget so both stay in sync with the same `placedSides`/bake
// pipeline. Returns a function rather than a single URL because the right
// overlay depends on which mesh is asking — `TexturedModel` calls it once per
// target mesh and gets back only that mesh's own placements baked in.
export function useDesignOverlayTexture(): (meshName: string) => Promise<string | null> {
  const { sides, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey } = useDesignStore();

  const placedSides = useMemo(() => sides.filter(s => s.glbBox), [sides]);
  const placementsKey = placedSides
    .map(s => { const b = s.glbBox!; return `${s.sideKey}:${b.left},${b.top},${b.width},${b.height},${b.rotationDeg},${b.targetMesh ?? ''}`; })
    .join('|');

  // Recreated whenever the underlying placements actually change — stale
  // closures over an old `placedSides`/`canvasInstances` would otherwise keep
  // baking yesterday's geometry forever, since `TexturedModel` only calls
  // this function, it never re-renders in response to the inputs changing.
  return useMemo(() => {
    return (meshName: string) => {
      if (!placedSides.length) return Promise.resolve(null);
      return buildCompositeDesignTexture(placedSides, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey, meshName).catch(() => null);
    };
    // `placementsKey` (not `placedSides` itself) is the real geometry dependency —
    // `sides` gets a new array reference on every store update even when nothing
    // relevant changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placedSides, canvasInstances, sideColorCountJsonCache, placementsKey]);
}
